import { useQueryClient, type Query } from "@tanstack/react-query";

import {
  AutoAcceptPaymentsStatus,
  getGetParticipantMeParticipantsMeGetQueryKey,
  useGetParticipantMeParticipantsMeGet,
  useUpdateMyAutoAcceptParticipantsMeAutoAcceptPaymentsPut,
} from "@/api-v3";

const PAYMENT_QUERY_PREFIXES = ["/payments", "/stipends", "/micropayments"];

const isPaymentQuery = (query: Query) => {
  const root = query.queryKey[0];
  const url = Array.isArray(root) ? root[0] : root;
  return (
    typeof url === "string" &&
    PAYMENT_QUERY_PREFIXES.some((prefix) => url.startsWith(prefix))
  );
};

interface SetEnabledOptions {
  onSuccess?: () => void;
}

export const useAutoAcceptPayments = () => {
  const queryClient = useQueryClient();

  const { data: participant, isPending: isParticipantPending } =
    useGetParticipantMeParticipantsMeGet();

  const participantKey = getGetParticipantMeParticipantsMeGetQueryKey();

  // A /participants/me refetch in flight during the save, such as one on window
  // focus, would otherwise resolve with the old status and overwrite this one.
  const mutation = useUpdateMyAutoAcceptParticipantsMeAutoAcceptPaymentsPut({
    mutation: {
      onMutate: () => queryClient.cancelQueries({ queryKey: participantKey }),
      onSuccess: async (updated, { data }) => {
        await queryClient.cancelQueries({ queryKey: participantKey });
        queryClient.setQueryData(participantKey, updated);
        if (data.enabled) {
          void queryClient.invalidateQueries({ predicate: isPaymentQuery });
        }
      },
    },
  });

  const setEnabled = (
    enabled: boolean,
    { onSuccess }: SetEnabledOptions = {}
  ) => {
    mutation.mutate({ data: { enabled } }, { onSuccess });
  };

  const status = participant?.auto_accept_payments_status;

  return {
    status,
    isEnabled: status === AutoAcceptPaymentsStatus.enabled,
    isLoading: isParticipantPending,
    isSaving: mutation.isPending,
    pendingValue: mutation.isPending
      ? mutation.variables?.data.enabled
      : undefined,
    setEnabled,
  };
};
